import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { authAPI } from '@/services/api/auth.api';
import { createForgotPasswordSchema, type ForgotPasswordFormData } from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import styles from './ForgotPasswordPage.module.scss';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);

  const forgotPasswordSchema = useMemo(() => createForgotPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      // El backend responde igual exista o no el email (anti-enumeración).
      await authAPI.forgotPassword(data.email);
      setIsSuccess(true);
    } catch {
      // Solo llega aquí ante un fallo real (red/servidor).
      setError('root', { message: t('auth.forgot.error') });
    }
  };

  return (
    <AuthLayout
      title={isSuccess ? t('auth.forgot.titleSuccess') : t('auth.forgot.title')}
      subtitle={isSuccess ? t('auth.forgot.subtitleSuccess') : t('auth.forgot.subtitle')}
    >
      {isSuccess ? (
        <div className={styles.forgotPassword__success}>
          <p className={styles.forgotPassword__success__message}>{t('auth.forgot.successMessage')}</p>
          <p className={styles.forgotPassword__success__note}>
            {t('auth.forgot.successNotePrefix')}{' '}
            <button
              type="button"
              data-no-press-scale=""
              onClick={() => setIsSuccess(false)}
              className={styles.forgotPassword__success__retry}
            >
              {t('auth.forgot.tryAgain')}
            </button>
          </p>
          <Button asChild variant="primary" size="lg" fullWidth>
            <Link to={ROUTES.AUTH.LOGIN}>{t('auth.forgot.backToLogin')}</Link>
          </Button>
        </div>
      ) : (
        <form className={styles.forgotPassword__form} onSubmit={handleSubmit(onSubmit)}>
          {errors.root && (
            <div className={styles.forgotPassword__error} role="alert">
              {errors.root.message}
            </div>
          )}

          <FormField
            label={t('auth.forgot.emailLabel')}
            type="email"
            placeholder={t('auth.forgot.emailPlaceholder')}
            error={errors.email?.message}
            required
            autoComplete="email"
            {...register('email')}
          />

          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting}>
            {t('auth.forgot.sendLink')}
          </Button>

          <Button asChild variant="ghost" size="md" fullWidth>
            <Link to={ROUTES.AUTH.LOGIN} className={styles.forgotPassword__back}>
              {t('auth.forgot.backToLogin')}
            </Link>
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
